import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaFileAlt, FaEnvelope, FaTimes } from 'react-icons/fa';

import config from "../config/config";
import authHeader from "../config/authHeader";

import { useFileManager } from "../contexts/FileManagerContext";

import useFetchIntervenants from "../hooks/useFetchIntervenants";

import FormattedInput from "../utils/FormattedInput";
import CustomSelect from "../utils/CustomSelect";
const DrawingEditor = lazy(() => import('../utils/DrawingEditor'));
const SpeechToText = lazy(() => import("../utils/SpeechToText"));

const DocumentUploadModal = ({ isVisible, onClose, onUploadSuccess = null, tabOpen = "drawing", uniqueUpload = false, lstDoxExt = [] }) => {
    const { uuid } = useParams();
    const { currentFolder, imageToEdit, setImageToEdit, noteToEdit, setNoteToEdit } = useFileManager();
    const { cas, fetchChargesAffairesByLicence } = useFetchIntervenants();

    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        fetchChargesAffairesByLicence();
    }, []);

    const currentDate = new Date().toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const [title, setTitle] = useState('');
    const [file, setFile] = useState([]);
    const [note, setNote] = useState({ title: `Note_${currentDate}`, text: '', type: 'Note' });
    const [drawing, setDrawing] = useState(uniqueUpload ? { title: `Annote_${currentDate}` } : { title: `Dessin_${currentDate}` });
    const [editorInstance, setEditorInstance] = useState(null);

    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploadStatusMessage, setUploadStatusMessage] = useState('');
    const [noteAttachments, setNoteAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);


    //listCAS

    const [listCasSelected, setSelectedCas] = useState([]);

    useEffect(() => {
        if (cas.length > 0) {
            setSelectedCas(cas.map((ca) => ({ UUID_: ca.UUID_, Nom: ca.Nom, selected: false })));
        }
    }, [cas]);

    const handleCasSelectionChange = (nom) => {
        setSelectedCas(prev =>
            prev.map(ca =>
                ca.Nom === nom ? { ...ca, selected: !ca.selected } : ca
            )
        );
    };


    const [selectedTab, setSelectedTab] = useState(null);

    // console.log("tabOpen", tabOpen);
    // console.log("imageToEdit", imageToEdit);
    // console.log("noteToEdit", noteToEdit);
    // console.log("note", note);

    useEffect(() => {
        setFile([]);
        setTitle("");
        setNoteAttachments([]);
        setSelectedCas(cas.map((ca) => ({ UUID_: ca.UUID_, Nom: ca.Nom, selected: false })));
        if (!noteToEdit) {
            setNote({ title: `Note_${currentDate}`, text: '', type: 'Note' });
        }
    }, [selectedTab]);

    useEffect(() => {
        if (noteToEdit != null) {
            setNote({
                title: noteToEdit.title.replace(".txt", ""),
                text: noteToEdit.body
            });
            setSelectedTab("note");
        } else if (imageToEdit != null) {
            setSelectedTab("drawing");
        } else {
            setSelectedTab(tabOpen);
        }
    }, [noteToEdit, imageToEdit, tabOpen]);

    useEffect(() => {
        if (!noteToEdit) {
            setExistingAttachments([]);
            return;
        }

        // Tous les docs qui ont uuid_jointe = UUID de la note
        const attachments = (lstDoxExt || []).filter(
            (doc) => doc.UUID_Externe_ === noteToEdit.UUID_
        );

        setExistingAttachments(attachments);
    }, [noteToEdit, lstDoxExt]);


    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFile((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFile((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const encodeFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result.split(",")[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    const getMimeTypeExtension = (mimeType) => {
        const parts = mimeType.split('/');
        return parts.length > 1 ? parts[1].toUpperCase() : '';
    };

    const getFileIcon = (type) => {
        switch (type) {
            case 'pdf': return <FaFilePdf className="text-red-600 text-5xl" />;
            case 'jpeg': return <FaFileImage className="text-blue-600 text-5xl" />;
            case 'svg': return <FaFileImage className="text-blue-600 text-5xl" />;
            case 'jpg': return <FaFileImage className="text-blue-600 text-5xl" />;
            case 'png': return <FaFileImage className="text-blue-600 text-5xl" />;
            case 'webp': return <FaFileImage className="text-blue-600 text-5xl" />;
            case 'docx': return <FaFileWord className="text-orange-600 text-5xl" />;
            case 'doc': return <FaFileWord className="text-orange-600 text-5xl" />;
            case 'xls': return <FaFileExcel className="text-green-600 text-5xl" />;
            case 'xlsx': return <FaFileExcel className="text-green-600 text-5xl" />;
            case 'eml': return <FaEnvelope className="text-green-600 text-5xl" />;
            case 'msg': return <FaEnvelope className="text-green-600 text-5xl" />;
            default: return <FaFileAlt className="text-gray-600 text-5xl" />;
        }
    };

    const compressImageIfNeeded = (file, maxSizeBytes = 1048576) => {
        return new Promise((resolve) => {
            if (!file.type.startsWith("image/") || file.size <= maxSizeBytes) {
                return resolve(file); // Pas besoin de compression
            }

            const img = new Image();
            const reader = new FileReader();

            reader.onload = (event) => {
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    const maxWidth = 1280;
                    const ratio = img.width / img.height;

                    canvas.width = maxWidth;
                    canvas.height = maxWidth / ratio;

                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    canvas.toBlob((blob) => {
                        const compressedFile = new File([blob], file.name, { type: "image/jpeg" });
                        resolve(compressedFile);
                    }, "image/jpeg", 0.8);
                };

                img.src = event.target.result;
            };

            reader.readAsDataURL(file);
        });
    };


    const [shouldSubmit, setShouldSubmit] = useState(false);

    const handleClose = () => {
        setFile([]);
        setIsFullscreen(false);
        setTitle("");
        setImageToEdit(null);
        setNoteToEdit(null);
        setNoteAttachments([]);
        setIsUploading(false);
        onClose();
    }

    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        setIsUploading(true);
        setUploadStatusMessage("Préparation des fichiers...");

        if (selectedTab === "note") {
            if (!note.text.trim()) {
                alert("La note ne peut pas être vide.");
                setIsUploading(false);
                setUploadStatusMessage("");
                return;
            }

            const encoder = new TextEncoder();
            const encodedText = encoder.encode(note.text);
            const noteBlob = new Blob([encodedText], { type: "text/plain" });
            const noteFile = new File([noteBlob], `${note.title}.txt`, { type: "text/plain" });

            const filesToUpload = [noteFile, ...noteAttachments];
            setFile(filesToUpload);
            setShouldSubmit(true);
            return;
        }

        if (selectedTab === "drawing") {
            if (!editorInstance) {
                alert("Erreur : L'éditeur de dessin n'est pas disponible.");
                setIsUploading(false);
                setUploadStatusMessage("");
                return;
            }

            const imageData = editorInstance.getImageData();

            if (!imageData) {
                alert("Erreur lors de l'exportation de l'image.");
                setIsUploading(false);
                setUploadStatusMessage("");
                return;
            }

            const canvas = document.createElement('canvas');
            canvas.width = imageData.width;
            canvas.height = imageData.height;
            const ctx = canvas.getContext('2d');
            const imgData = new ImageData(imageData.data, imageData.width, imageData.height);
            ctx.putImageData(imgData, 0, 0);

            const dataUrl = canvas.toDataURL('image/png');
            const byteArray = Uint8Array.from(atob(dataUrl.split(',')[1]), c => c.charCodeAt(0));
            const blob = new Blob([byteArray], { type: "image/png" });

            const drawingFile = new File([blob], `${drawing.title || "dessin"}.png`, { type: "image/png" });
            setFile([drawingFile]);
            setShouldSubmit(true);
            return;
        }

        if (!file || file.length === 0) {
            alert("Il faut au moins un fichier à uploader.");
            setIsUploading(false);
            setUploadStatusMessage("");
            return;
        }

        await uploadFiles();
    };


    useEffect(() => {
        if (shouldSubmit && file.length > 0) {
            setShouldSubmit(false);
            uploadFiles();
        }
    }, [file]);

    const uploadFiles = async () => {
        try {
            setIsUploading(true);
            setProgress(0);

            const compressedFiles = await Promise.all(
                file.map((f) => compressImageIfNeeded(f))
            );

            const base64Files = await Promise.all(
                compressedFiles.map((f) => encodeFileToBase64(f))
            );

            // d'abord le note, puis les pièces jointes si nécessaire
            if (selectedTab === "note") {
                const [noteFileCompressed, ...attachmentsCompressed] = compressedFiles;
                const [noteBase64, ...attachmentsBase64] = base64Files;

                if (noteToEdit) {
                    await updateFile(noteFileCompressed, noteBase64);

                    const pieceAJoindre = noteToEdit.UUID_;
                    // Nouvelles pièces jointes liées à la note existante
                    for (let index = 0; index < attachmentsCompressed.length; index++) {
                        await uploadFile(
                            attachmentsCompressed[index],
                            attachmentsBase64[index],
                            index + 1,
                            attachmentsCompressed.length,
                            pieceAJoindre
                        );
                    }

                    if (typeof onUploadSuccess === "function") {
                        onUploadSuccess(pieceAJoindre);
                    }
                } else {
                    // CREATE NOTE : on crée d'abord la note,
                    // puis on utilise son UUID pour lier les pièces jointes
                    const noteUploadResult = await uploadFile(
                        noteFileCompressed,
                        noteBase64,
                        1,
                        compressedFiles.length
                    );
                    const pieceAJoindre = noteUploadResult.createdUuid;
                    for (let index = 0; index < attachmentsCompressed.length; index++) {
                        await uploadFile(
                            attachmentsCompressed[index],
                            attachmentsBase64[index],
                            index + 2,
                            compressedFiles.length,
                            pieceAJoindre
                        );
                    }

                    if (typeof onUploadSuccess === "function") {
                        onUploadSuccess(pieceAJoindre || null);
                    }
                }
            } else {
                // AUTRES TABS : comportement existant (fichiers normaux)
                if (noteToEdit) {
                    await updateFile(compressedFiles[0], base64Files[0]);
                    if (typeof onUploadSuccess === "function") {
                        onUploadSuccess(noteToEdit.UUID_);
                    }
                } else {
                    for (let index = 0; index < compressedFiles.length; index++) {
                        await uploadFile(
                            compressedFiles[index],
                            base64Files[index],
                            index + 1,
                            compressedFiles.length,
                        );
                    }

                    if (typeof onUploadSuccess === "function") {
                        onUploadSuccess(null);
                    }
                }
            }
        } catch (error) {
            console.error("Erreur:", error);
            alert("Une erreur est survenue lors de l'upload.");
        } finally {
            setIsUploading(false);
            setProgress(0);
            setTitle("");
            setFile([]);
            setNoteAttachments([]);
            setSelectedCas(cas.map((ca) => ({ UUID_: ca.UUID_, Nom: ca.Nom, selected: false })));
            setNote({ title: `Note_${currentDate}`, text: '', type: 'Note' });
            setDrawing(uniqueUpload ? { title: `Annote_${currentDate}` } : { title: `Dessin_${currentDate}` });
            setImageToEdit(null);
            setNoteToEdit(null);
            onClose();
        }
    };



    const uploadFile = (f, base64Data, currentIndex, totalFiles, pieceAJoindre = null) => {
        return new Promise((resolve, reject) => {
            setUploadStatusMessage(`Upload du fichier : ${f.name} (${currentIndex}/${totalFiles})...`);

            const formData = new FormData();
            formData.append("title", f.name);
            formData.append("fileblob", base64Data);
            formData.append("uuid", uuid);
            formData.append("type", getMimeTypeExtension(f.type));
            formData.append("dossier", currentFolder);
            formData.append("size", f.size);
            if (pieceAJoindre) {
                formData.append("UUID_Jointe", pieceAJoindre);
            }
            const notifInfo = {
                Destinataires: listCasSelected
                    .filter(ca => ca.selected)
                    .map(ca => ({
                        UUID_: ca.UUID_,
                        Nom: ca.Nom
                    })),
                Origine_Table: "Documents_Externes",
                Commentaire: "vous a notifié lors de la création de ce document:",
            };

            console.log("notifInfo", notifInfo);

            formData.append("notifInfo", JSON.stringify(notifInfo));

            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${config.apiBaseUrl}/4DACTION/KST_createDoxExt`, true);

            const headers = authHeader();
            for (const key in headers) {
                xhr.setRequestHeader(key, headers[key]);
            }

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentForThisFile = (event.loaded / event.total) * 100;
                    const totalProgress = ((currentIndex - 1 + (percentForThisFile / 100)) / totalFiles) * 100;
                    setProgress(Math.round(totalProgress));
                    setUploadStatusMessage(`Transfert de ${f.name}... ${Math.round(percentForThisFile)}%`);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    setUploadStatusMessage(`Fichier ${f.name} terminé`);

                    let createdUuid = null;
                    try {
                        const json = JSON.parse(xhr.response);
                        createdUuid = json.UUID_ || json.uuid || null;
                    } catch (e) {
                    }

                    resolve({ raw: xhr.response, createdUuid });
                } else {
                    reject(new Error(`Erreur serveur (${xhr.status})`));
                }
            };

            xhr.onerror = () => reject(new Error("Erreur réseau"));
            xhr.send(formData);
        });
    };


    const updateFile = async (f, base64Data) => {
        console.log("updateFile", f, base64Data);

        const formData = new FormData();
        formData.append("uuid", noteToEdit.UUID_);
        formData.append("rename", f.name);
        formData.append("fileblob", base64Data);
        formData.append("size", f.size);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_editDoxExt`, {
                method: "POST",
                headers: authHeader(),
                body: formData,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Erreur serveur (${response.status})`);
            }
        } catch (error) {
            if (error.name === "AbortError") {
                alert(`La modification de ${f.name} a été annulée (timeout).`);
            } else {
                alert(`Erreur lors de la modification de ${f.name}: ${error.message}`);
            }
            console.error("Erreur update :", error);
        }
    };

    const handleNoteAttachmentChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setNoteAttachments((prev) => [...prev, ...selectedFiles]);
    };

    const handleRemoveNoteAttachment = (index) => {
        setNoteAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer.files;
        setFile((prevFiles) => [...prevFiles, ...droppedFile]);
    };

    const truncateFileName = (name, maxLength = 20) => {
    if (!name) return "";

    const dotIndex = name.lastIndexOf(".");
    if (dotIndex === -1) {
        return name.length > maxLength
            ? name.slice(0, maxLength - 3) + "..."
            : name;
    }

    const base = name.slice(0, dotIndex);      
    const ext = name.slice(dotIndex);          

    const maxBaseLength = maxLength - ext.length - 3;
    if (maxBaseLength <= 0) {
        return name.length > maxLength
            ? name.slice(0, maxLength - 3) + "..."
            : name;
    }

    const truncatedBase =
        base.length > maxBaseLength
            ? base.slice(0, maxBaseLength) + "..."
            : base;

    return truncatedBase + ext;
};

    //NOTE

    const handleNoteChange = (key, value) => {
        if (key === "title") {
            if (value.length > 50 || /[\n\r\t\/\\*?"<>|]/.test(value)) {
                return;
            }

            value = value.trim() === "" ? `${note.type}_${currentDate}` : value;
        }

        setNote({
            ...note,
            [key]: value,
        });
    };

    const onChangeSelectTypeNote = (value) => {
        const newTitle = note.title.replace(note.type, value);
        setNote({
            ...note,
            type: value,
            title: newTitle,
        });
    };

    const handleRemoveExistingAttachment = async (attachment) => {
        const confirmDelete = window.confirm(
            `Supprimer la pièce jointe "${attachment.Nom_Du_Document || attachment.name}" ?`
        );
        if (!confirmDelete) return;

        try {
            const formData = new FormData();
            formData.append("uuid", attachment.UUID_);

            const response = await fetch(
                `${config.apiBaseUrl}/4DACTION/KST_deleteDoxExt`,
                {
                    method: "POST",
                    headers: authHeader(),
                    body: formData,
                }
            );

            const data = await response.json();

            if (data.status === "success") {
                // On la retire de la liste locale
                setExistingAttachments((prev) =>
                    prev.filter((att) => att.UUID_ !== attachment.UUID_)
                );

                // On demande au parent de rafraîchir les documents externes
                if (typeof onUploadSuccess === "function" && noteToEdit) {
                    onUploadSuccess(noteToEdit.UUID_);
                }
            } else {
                console.error("Erreur suppression pièce jointe :", data);
                alert("Impossible de supprimer cette pièce jointe.");
            }
        } catch (err) {
            console.error("Erreur lors de la suppression de la pièce jointe :", err);
            alert("Erreur réseau lors de la suppression de la pièce jointe.");
        }
    };

    

    //DRAWING

    const handleDrawingChange = (key, value) => {
        if (key === "title") {
            if (value.length > 50 || /[\n\r\t\/\\*?"<>|]/.test(value)) {
                return;
            }
        }

        setDrawing({
            ...drawing,
            [key]: value,
        });
    };

    // useEffect(() => {
    //     if (noteToEdit && note.text === '') {
    //         setNote({
    //             title: noteToEdit.title.,
    //             text: noteToEdit.body,
    //         });
    //     }
    // }, [noteToEdit]);

    // console.log("listCasSelected", listCasSelected);

    return (

        <div
            className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center transition-opacity duration-300 ${isVisible || imageToEdit || noteToEdit ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={(e) => {
                const selection = window.getSelection();
                if (selection && selection.type === 'Range') return;
                if (e.target === e.currentTarget) {
                    handleClose();
                    setImageToEdit(null);
                    setNoteToEdit(null);
                }
            }}
        >
            <div
                className={`transition-all duration-700 ease-in-out bg-white overflow-hidden flex flex-col relative ${isFullscreen
                    ? 'w-[95vw] h-[95vh] p-10 rounded-none'
                    : 'w-[53rem] max-h-[80vh] p-6 rounded-xl'
                    }`}
            >
                {/* BOUTONS TABS */}
                {!imageToEdit && !noteToEdit && (
                    <div className="flex flex-nowrap justify-center items-center space-x-2 mb-4 text-white">
                        {['fileUpload', 'note', 'drawing'].map((tab) => (
                            <button
                                key={tab}
                                className={`py-3 px-10 text-sm font-semibold rounded-lg ${selectedTab === tab
                                    ? 'bg-orange-600 border-b-2 border-orange-500 text-white'
                                    : 'text-gray-600 bg-gray-100 hover:text-orange-400'
                                    }`}
                                onClick={() => setSelectedTab(tab)}
                            >
                                {tab === 'fileUpload' ? 'Fichier' : tab === 'note' ? 'Note' : 'Dessin'}
                            </button>
                        ))}
                    </div>
                )}

                {/* BOUTON FULLSCREEN */}
                <div className="top-6 left-6 z-10 absolute">
                    <button
                        type="button"
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="group flex justify-center items-center bg-orange-600 px-3 py-2 rounded-xl overflow-hidden text-white text-sm transition-all duration-300"
                    >
                        <i className={`fa-solid ${isFullscreen ? 'fa-minimize' : 'fa-maximize'}`}></i>
                        <span className="opacity-0 group-hover:opacity-100 group-hover:ml-2 max-w-0 group-hover:max-w-xs whitespace-nowrap transition-all duration-700 ease-in-out">
                            {isFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'}
                        </span>
                    </button>
                </div>

                {/* BOUTON FERMER */}
                <div className="top-6 right-6 z-10 absolute">
                    <button
                        type="button"
                        onClick={() => onClose()}
                        className="group flex justify-center items-center bg-red-500 px-3 py-2 rounded-xl overflow-hidden text-white text-sm transition-all duration-300"
                    >
                        <i className={`fa-solid fa-xmark fa-xl`}></i>
                        <span className="opacity-0 max-w-0 whitespace-nowrap transition-all duration-700 ease-in-out">
                            Fermer
                        </span>
                    </button>
                </div>

                {/* CONTENU */}
                <div className="flex-grow overflow-y-auto">
                    {selectedTab === "fileUpload" && (
                        <>
                            <div className="text-center">
                                <h2 className="mt-5 font-bold text-gray-900 text-3xl">Import de Fichier</h2>
                                <p className="mt-2 text-gray-400 text-sm">
                                    Déposez vos fichier pour l'enregistrer dans la base de données.
                                </p>
                                <p className="mt-2 text-gray-400 text-sm">Upload vers le dossier : <span className="font-bold text-base">{currentFolder || 'Documents Externes'}</span></p>
                                <div className="flex flex flex-col justify-center items-center gap-2 mt-2 text-gray-400 text-sm">
                                    Informez les Utilisateurs suivants :
                                    <span className="w-68 font-semibold text-gray-600 text-base">
                                        <CustomSelect value={`Utilisateurs séléctionnés (${listCasSelected.filter(ca => ca.selected).length})`}
                                            options={listCasSelected} onChange={handleCasSelectionChange} multiple={true}
                                        />
                                    </span>
                                </div>
                            </div>

                            <form className="space-y-3 mt-8" onSubmit={handleUploadSubmit}>
                                <div>
                                    <div className="space-y-2 grid grid-cols-1">
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                    <div className="space-y-2 grid grid-cols-1">
                                        <label className="font-bold text-gray-500 text-sm tracking-wide">
                                            Zone de Document
                                        </label>
                                        <div className="flex justify-center items-center w-full">
                                            <label
                                                className={`group flex flex-col border-4 border-dashed rounded-lg text-center
                                                    ${isFullscreen ? 'p-16 h-[500px]' : 'p-10 h-60'} w-full`}
                                            >
                                                <div className="flex flex-col justify-center items-center w-full h-full text-center">
                                                    <div className="flex flex-col items-center">
                                                        {file.length > 0 ? (
                                                            <div className={`flex ${file.length === 1 ? 'justify-center' : 'flex-wrap'}`}>
                                                                {file.map((file, index) => {
                                                                    const fileExtension = getMimeTypeExtension(file.type).toLowerCase();
                                                                    const isImage = fileExtension === "image" || fileExtension === "png" || fileExtension === "jpg" || fileExtension === "jpeg";

                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            className="group relative flex flex-col items-center gap-2 mx-2 my-2"
                                                                        >
                                                                            {isImage ? (
                                                                                <img
                                                                                    src={URL.createObjectURL(file)}
                                                                                    alt={file.name}
                                                                                    className="rounded-md w-32 object-cover"
                                                                                />
                                                                            ) : (
                                                                                getFileIcon(fileExtension)
                                                                            )}
                                                                            <p className="text-gray-800 text-sm text-center">
                                                                                {truncateFileName(file.name)}
                                                                            </p>
                                                                            <a
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleRemoveFile(index);
                                                                                }}
                                                                                className="top-0 right-0 absolute opacity-0 group-hover:opacity-100 text-red-500 text-xl transition-opacity duration-200"
                                                                            >
                                                                                <FaTimes />
                                                                            </a>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <img
                                                                    className="h-36 object-center"
                                                                    src="https://img.freepik.com/free-vector/image-upload-concept-landing-page_52683-27130.jpg?size=338&ext=jpg"
                                                                    alt="upload illustration"
                                                                />
                                                                <p className="text-gray-500 pointer-none">
                                                                    <span className="text-sm">
                                                                        Faites glisser et déposez les fichiers ici<br /> ou{" "}
                                                                        <a href="#" className="text-orange-600 hover:underline">
                                                                            sélectionnez des fichiers
                                                                        </a>{" "}
                                                                        depuis votre appareil
                                                                    </span>
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    onChange={handleFileChange}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                    <p className="text-gray-300 text-sm">
                                        <span>Fichier type: doc, pdf, images</span>
                                    </p>
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        className={`relative overflow-hidden flex justify-center items-center gap-2 text-white shadow-lg my-5 p-4 rounded-full w-full font-semibold tracking-wide transition-all duration-300 ease-in 
                                        ${isUploading ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
                                        disabled={isUploading}
                                    >
                                        {isUploading && (
                                            <span
                                                className="top-0 left-0 z-0 absolute opacity-40 h-full pointer-events-none wave-progress"
                                                style={{ width: `${progress}%`, transition: 'width 0.2s ease-in-out' }}
                                            />
                                        )}

                                        <span className="z-10 flex items-center gap-2">
                                            {isUploading ? (
                                                <>
                                                    {uploadStatusMessage}
                                                    <i className="animate-spin fas fa-spinner" />
                                                </>
                                            ) : (
                                                "Importer"
                                            )}
                                        </span>
                                    </button>
                                </div>
                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="mt-2 text-gray-500 text-sm hover:underline"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </>
                    ) || selectedTab === "note" && (
                        <div class={`flex flex-col ${isFullscreen ? 'w-full h-full' : 'w-full h-full'}`}>
                            <div className="text-center">
                                <h2 className="mt-5 font-bold text-gray-900 text-3xl">Zone de Note</h2>

                                <p className="mt-2 text-gray-400 text-sm">
                                    Rédigez votre note et enregistrez-la dans les documents externes.
                                </p>
                                <p className="flex justify-center items-center gap-2 mt-2 text-gray-400 text-sm">Uploader vers le dossier : <span className="w-48 font-bold text-gray-600 text-base">
                                    {currentFolder || 'Documents Externes'}
                                </span></p>
                                <div className="flex justify-center items-center gap-2 mt-2 text-gray-400 text-sm">
                                    Enregistrer en tant que :
                                    <span className="w-28 font-bold text-gray-600 text-base">
                                        <CustomSelect value={note.type || "Note"}
                                            options={["Note", "Devis", "Rapport", "Autre"]}
                                            onChange={(newValue) => onChangeSelectTypeNote(newValue)}
                                        />
                                    </span>
                                    sous le nom de :
                                    <span className="font-bold text-gray-600 text-base">
                                        <FormattedInput value={note.title} onChange={(newValue) => handleNoteChange("title", newValue)} type='text'></FormattedInput>
                                    </span>
                                </div>
                                <div className="flex flex justify-center items-center gap-2 mt-2 text-gray-400 text-sm">
                                    Informez les Utilisateurs suivants :
                                    <span className="w-68 font-semibold text-gray-600 text-base">
                                        <CustomSelect value={`Utilisateurs séléctionnés (${listCasSelected.filter(ca => ca.selected).length})`}
                                            options={listCasSelected} onChange={handleCasSelectionChange} multiple={true}
                                        />
                                    </span>
                                </div>
                            </div>
                            <div class="flex flex-col mt-5 overflow-hidden grow">
                                <div className="flex items-center gap-4">
                                    <label className="font-bold text-gray-500 text-sm tracking-wide">
                                        Zone de Texte
                                    </label>

                                    <Suspense
                                        fallback={
                                            <div className="text-gray-400 text-sm">
                                                Chargement de la dictée vocale…
                                            </div>
                                        }
                                    >
                                        <SpeechToText
                                            onTextChange={(text) => handleNoteChange("text", text)}
                                        />
                                    </Suspense>
                                </div>
                                <div className="flex-grow mt-2 overflow-hidden">
                                    <textarea
                                        placeholder="Ajoutez votre note..."
                                        value={note.text}
                                        onChange={(e) => handleNoteChange("text", e.target.value)}
                                        className={`p-2 border-2 border-gray-200 rounded-md focus:outline-1 focus:outline-orange-600 w-full font-semibold resize-none ${isFullscreen ? 'flex-grow h-full' : 'h-[60]'}`}
                                    />
                                </div>
                            </div>
                            {noteToEdit && existingAttachments.length > 0 && (
                                <div className="mt-6">
                                    <label className="font-bold text-gray-500 text-sm tracking-wide">
                                        Pièces jointes existantes
                                    </label>

                                    <div className="mt-2 flex flex-wrap gap-3">
                                        {existingAttachments.map((att) => (
                                            <div
                                                key={att.UUID_}
                                                className="group relative flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
                                            >
                                                <span className="text-sm text-gray-800">
                                                    {truncateFileName(att.Nom_Du_Document|| att.name)}
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveExistingAttachment(att)}
                                                    className="text-red-500 text-xs hover:underline"
                                                >
                                                    Supprimer
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-4">
                                <label className="font-bold text-gray-500 text-sm tracking-wide">
                                    Pièces jointes de la note
                                </label>

                                <div className="mt-2 border-2 border-dashed border-gray-200 rounded-md p-4 text-center">
                                    <input
                                        id="note-attachments-input"
                                        type="file"
                                        multiple
                                        className="hidden"
                                        onChange={handleNoteAttachmentChange}
                                    />
                                    <label
                                        htmlFor="note-attachments-input"
                                        className="cursor-pointer text-orange-600 hover:underline"
                                    >
                                        Ajouter un ou plusieurs documents
                                    </label>

                                    {noteAttachments.length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                            {noteAttachments.map((f, index) => (
                                                <div
                                                    key={index}
                                                    className="group relative flex flex-col items-center gap-1 mx-2 my-1"
                                                >
                                                    {getFileIcon(getMimeTypeExtension(f.type).toLowerCase())}
                                                    <p className="text-gray-800 text-xs text-center">
                                                        {truncateFileName(f.name, 30)}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveNoteAttachment(index)}
                                                        className="top-0 right-0 absolute opacity-0 group-hover:opacity-100 text-red-500 text-xs transition-opacity duration-200"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <button
                                    onClick={handleUploadSubmit}
                                    className={`relative overflow-hidden flex justify-center items-center gap-2 text-white shadow-lg my-5 p-4 rounded-full w-full font-semibold tracking-wide transition-all duration-300 ease-in 
                                        ${isUploading ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
                                    disabled={isUploading}
                                >
                                    {isUploading && (
                                        <span
                                            className="top-0 left-0 z-0 absolute opacity-40 h-full pointer-events-none wave-progress"
                                            style={{ width: `${progress}%`, transition: 'width 0.2s ease-in-out' }}
                                        />
                                    )}

                                    <span className="z-10 flex items-center gap-2">
                                        {isUploading ? (
                                            <>
                                                {uploadStatusMessage}
                                                <i className="animate-spin fas fa-spinner" />
                                            </>
                                        ) :
                                            !noteToEdit ? 'Ajouter la note' : 'Modifier la note'
                                        }
                                    </span>
                                </button>
                            </div>
                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="mt-2 text-gray-500 text-sm hover:underline"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    ) || selectedTab === "drawing" && (
                        <div className={`${isFullscreen ? 'w-full h-full flex flex-col overflow-hidden' : 'w-full h-full flex flex-col overflow-hidden'}`}>
                            <div className="text-center">
                                <h2 className="mt-5 font-bold text-gray-900 text-3xl">Table de Dessin</h2>
                                <p className="mt-2 text-gray-400 text-sm">
                                    Dessinez votre note et enregistrez-la dans les documents externes.
                                </p>
                                <p className="flex justify-center items-center gap-2 mt-2 text-gray-400 text-sm">Uploader vers le dossier : <span className="w-48 font-bold text-gray-600 text-base">
                                    {currentFolder || 'Documents Externes'}
                                </span></p>
                                <div className="flex justify-center items-center gap-2 mt-2 text-gray-400 text-sm">
                                    Enregistrer
                                    sous le nom de :
                                    <span className="font-bold text-gray-600 text-base">
                                        <FormattedInput value={drawing.title} onChange={(newValue) => handleDrawingChange("title", newValue)} type='text'></FormattedInput>
                                    </span>
                                </div>
                                <div className="flex flex justify-center items-center gap-2 mt-2 text-gray-400 text-sm">
                                    Informez les Utilisateurs suivants :
                                    <span className="w-68 font-semibold text-gray-600 text-base">
                                        <CustomSelect value={`Utilisateurs séléctionnés (${listCasSelected.filter(ca => ca.selected).length})`}
                                            options={listCasSelected} onChange={handleCasSelectionChange} multiple={true}
                                        />
                                    </span>
                                </div>
                            </div>
                            <div class="mt-2">
                                <div className="space-y-2 grid grid-cols-1">
                                    <Suspense
                                        fallback={
                                            <div className="p-6 text-gray-500 text-sm text-center">
                                                Chargement de l’éditeur de dessin…
                                            </div>
                                        }
                                    >
                                        <div className="flex justify-center items-center bg-gray-100 rounded w-full">
                                            <DrawingEditor setEditorInstance={setEditorInstance} imageSrc={imageToEdit || null} ></DrawingEditor>
                                        </div>
                                    </Suspense>
                                </div>
                            </div>

                            <div>
                                <button
                                    onClick={handleUploadSubmit}
                                    className={`relative overflow-hidden flex justify-center items-center gap-2 text-white shadow-lg my-5 p-4 rounded-full w-full font-semibold tracking-wide transition-all duration-300 ease-in 
                                        ${isUploading ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
                                    disabled={isUploading}
                                >
                                    {isUploading && (
                                        <span
                                            className="top-0 left-0 z-0 absolute opacity-40 h-full pointer-events-none wave-progress"
                                            style={{ width: `${progress}%`, transition: 'width 0.2s ease-in-out' }}
                                        />
                                    )}

                                    <span className="z-10 flex items-center gap-2">
                                        {isUploading ? (
                                            <>
                                                {uploadStatusMessage}
                                                <i className="animate-spin fas fa-spinner" />
                                            </>
                                        ) : (
                                            "Ajouter le dessin"
                                        )}
                                    </span>
                                </button>
                            </div>
                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="mt-2 text-gray-500 text-sm hover:underline"
                                >
                                    Annuler
                                </button>


                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentUploadModal;
